import { z } from 'zod';

/**
 * « Envoyer à mon nom » : aucun serveur, aucune boîte connectée. Le mail part
 * par Innov'Paie sous le nom saisi, et « Répondre » va à l'adresse saisie.
 * Côté API, un expéditeur sans SMTP ni OAuth emprunte déjà ce chemin
 * (mail.service, buildDocumentMailOptions) : seul l'écran manquait.
 */
export const SANS_BOITE_PROVIDER_ID = 'envoi-a-mon-nom';

const optionalEmailSchema = z.preprocess(
  value => (value === '' ? undefined : value),
  z.string().email('Email invalide').optional(),
);

export const smtpConfigBaseSchema = z.object({
  smtpProvider: z.string().optional(),
  smtpHost: z.string().optional(),
  smtpPort: z.number().min(1).max(65535, 'Port invalide').optional(),
  smtpSsl: z.boolean().optional(),
  smtpTls: z.boolean().optional(),
  smtpUser: z.string().optional(),
  smtpPassword: z.string().optional(),
  smtpFromEmail: optionalEmailSchema,
  smtpFromName: z.string().optional(),
});

export const smtpConfigRefine = (
  data: z.infer<typeof smtpConfigBaseSchema>,
  ctx: z.RefinementCtx,
) => {
  if (!data.smtpProvider) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['smtpProvider'],
      message: 'Choisissez comment envoyer vos emails',
    });
    return;
  }

  // Skip SMTP field validation for OAuth providers
  if (
    data.smtpProvider === 'microsoft365-oauth' ||
    data.smtpProvider === 'gmail-oauth'
  ) {
    return;
  }

  if (data.smtpProvider === SANS_BOITE_PROVIDER_ID) {
    if (!data.smtpFromName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['smtpFromName'],
        message: 'Le nom affiché est requis',
      });
    }
    if (!data.smtpFromEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['smtpFromEmail'],
        message: "L'adresse de réponse est requise",
      });
    }
    return;
  }

  if (!data.smtpHost) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['smtpHost'],
      message: 'Le serveur SMTP est requis',
    });
  }
  if (!data.smtpPort) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['smtpPort'],
      message: 'Le port SMTP est requis',
    });
  }
  if (!data.smtpUser) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['smtpUser'],
      message: "L'adresse email est requise",
    });
  }
  if (!data.smtpPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['smtpPassword'],
      message: 'Le mot de passe est requis',
    });
  }
};

export const smtpConfigSchema = smtpConfigBaseSchema.superRefine(smtpConfigRefine);

export type SmtpConfigFormData = z.infer<typeof smtpConfigBaseSchema>;
